import { TirvuFace, type DetectedFace, type Tile, type TileSpec } from '@/ml/tirvu-face';

import Api from './Api';
import { FaceAntiSpoof, SPEC_ANTISPOOF } from './FaceAntiSpoof';
import { FaceExpression, SPEC_EXPRESSAO } from './FaceExpression';
import { FaceRecognition, SPEC_RECONHECIMENTO, type Qualidade } from './FaceRecognition';

/**
 * Orquestrador da biometria facial — o ponto de entrada das telas.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * POR QUE EXISTE
 * ─────────────────────────────────────────────────────────────────────────────
 * O fluxo antigo passava a foto por CINCO etapas independentes (flip → detecção →
 * anti-spoof → reconhecimento → expressão) e cada uma refazia o mesmo trabalho caro:
 * decodificar a foto, recortar via ImageManipulator, gerar JPEG, virar base64,
 * decodificar de novo com jpeg-js. Isso custava segundos de CPU e era a origem do
 * OOM que FECHAVA o app na captura em aparelho de sensor grande (há comentário sobre
 * o POCO X5 Pro em `screens/Camera.tsx`).
 *
 * Aqui é UMA detecção e UMA chamada nativa que devolve os três tiles prontos. A foto
 * é decodificada duas vezes no total (uma no detect, uma no extract), em nativo, com
 * teto de resolução — nunca mais em JS.
 */

export type ResultadoCadastro = {
  ok: boolean;
  motivo?: string;
  /** Score de ATAQUE do anti-spoof (alto = fraude provável). null se não foi rodado. */
  spoofScore: number | null;
  qualidade: Qualidade | null;
};

export type ResultadoVerificacao = {
  ok: boolean;
  motivo?: string;
  similarity: number;
  /** true = zona cinza: provavelmente a pessoa certa com foto ruim, não impostor. */
  duvidoso: boolean;
  /** false quando não há biometria cadastrada neste aparelho (ou é de versão antiga). */
  temReferencia: boolean;
  spoofScore: number | null;
  expressao: string | null;
  qualidade: Qualidade | null;
};

type Opcoes = {
  /** Rosto já detectado (evita uma segunda detecção quando a tela acabou de fazer uma). */
  face?: DetectedFace;
  antiSpoof?: boolean;
  expressao?: boolean;
};

/**
 * Quem está cadastrando. Sem isto a biometria fica só no aparelho.
 *
 * A tela é quem sabe disso (vem do `userData` do AuthContext), então o serviço
 * recebe pronto em vez de importar o contexto — um serviço que depende de React
 * não dá para testar nem chamar de fora de uma tela. Quem monta é
 * `utils/biometriaIdentidade.ts`, fonte única das duas telas de CLT.
 *
 * ⚠️ Sem `id_adm_cliente`: a linha é gravada com posto e dispositivo ZERADOS (ver o
 * payload em `cadastrar`). O campo existia aqui, era preenchido com o posto da pessoa
 * e DESCARTADO na gravação — a pior combinação possível, porque parecia significar
 * algo.
 */
export type IdentidadeColaborador = {
  id_adm_colaborador: number;
  cpf: string;
};

/**
 * Quantos frames a prova de vida MEDE.
 *
 * Com a recusa desligada (ver `PROVA_DE_VIDA_ATIVA` em `services/FaceAntiSpoof.ts`),
 * o que estes frames produzem hoje é AMOSTRA para o log — a mediana e a lista de
 * scores que vão alimentar a recalibração quando houver dado de campo.
 *
 * ⚠️ NÃO baixe para 1, mesmo desligada. A distribuição do modelo dentro de um mesmo
 * rosto vivo é larga (−0,006 a 0,323 no conjunto medido); uma amostra só não diz nada
 * sobre a pessoa e não serve para recalibrar. E se a recusa for religada, ela depende
 * de UNANIMIDADE — que não existe com um voto.
 *
 * `min(3, quantos vierem)`: quem chama com uma foto só (fluxo do ponto, no primeiro
 * registro) mede uma. Nunca pior do que antes.
 */
const FRAMES_PROVA_VIDA = 3;

const detectarMaior = async (imageUri: string): Promise<DetectedFace> => {
  const res = await TirvuFace.detect(imageUri);
  const face = res.faces[0]; // o nativo devolve ordenado por área
  if (!face) throw new Error('Nenhum rosto identificado na foto');
  if (!face.landmarks) {
    throw new Error('Não foi possível localizar os pontos do rosto — melhore a iluminação');
  }
  return face;
};

export const FaceBiometria = {
  /** false em Expo Go / build sem o nativo. O chamador deve degradar, não quebrar. */
  disponivel: (): boolean => TirvuFace.isAvailable(),

  /**
   * Pré-carrega os três modelos. Chamar no login/foco da câmera.
   *
   * EM SÉRIE, não em paralelo: carregar os 3 interpretadores (~10 MB de pesos) ao
   * mesmo tempo cria um PICO de alocação nativa que, em aparelho low-RAM, dispara o
   * lowmemorykiller — o app simplesmente "fecha". Sequencial dá o mesmo resultado com
   * pico menor. (Mesma razão documentada no warmup de `screens/Camera.tsx`.)
   */
  warmup: async (): Promise<void> => {
    for (const carregar of [
      FaceExpression.warmup,
      FaceRecognition.warmup,
      FaceAntiSpoof.warmup,
    ]) {
      try {
        await carregar();
      } catch {
        /* modelo ausente não pode derrubar o warmup dos outros */
      }
    }
  },

  /**
   * Busca no servidor a face já cadastrada do colaborador e a registra neste aparelho.
   *
   *   'importado'    → há referência local agora; quem chamou marca o setup como feito;
   *   'sem_cadastro' → o servidor não tem face dessa pessoa: segue para o cadastro;
   *   'falhou'       → sem rede/erro/foto inservível: NÃO decide nada — quem chamou
   *                    segue o caminho de antes (cadastro), e o boot tenta de novo depois.
   * Nunca lança.
   */
  importarDoServidor: async (): Promise<'importado' | 'sem_cadastro' | 'falhou'> => {
    try {
      const r = await Api.geral.biometriaColaborador();
      if (!r?.success) {
        console.warn('[FaceBiometria] biometriaColaborador falhou:', r?.erros ?? r?.offline);
        return 'falhou';
      }
      const face = r.done;
      if (!face?.possui) return 'sem_cadastro';
      const res = await FaceRecognition.importarDoServidor(face);
      return res.importado ? 'importado' : 'falhou';
    } catch (err) {
      console.warn('[FaceBiometria] importarDoServidor:', err);
      return 'falhou';
    }
  },

  /**
   * Cadastra a biometria a partir de uma ou mais fotos.
   *
   * Portão de qualidade ESTRITO: uma referência ruim é permanente e quebraria o ponto
   * do colaborador para sempre. Melhor recusar e pedir outra foto agora.
   *
   * Passar várias URIs (ângulos/luz diferentes) é o caminho recomendado — cada uma
   * vira um template e a verificação compara contra o melhor deles.
   */
  cadastrar: async (
    imageUris: string[],
    opts?: Opcoes & { identidade?: IdentidadeColaborador },
  ): Promise<ResultadoCadastro> => {
    try {
      let spoofScore: number | null = null;

      /**
       * Prova de vida — DEPOIS do portão de qualidade e ANTES de gravar.
       *
       * ⚠️ HOJE ELA NÃO RECUSA NINGUÉM: só mede e registra. O porquê está em
       * `PROVA_DE_VIDA_ATIVA` (`services/FaceAntiSpoof.ts`) — o modelo pontua por
       * PESSOA, não por vivacidade, e nenhum dos limiares tentados (0,90 por maioria,
       * depois 0,99 por unanimidade) parou de barrar colaborador legítimo. O TirvuTab
       * percorreu a mesma escada e desligou pela mesma razão.
       *
       * O bloco abaixo é o histórico de como se chegou aqui — vale manter porque
       * explica por que a ORDEM (qualidade primeiro) continua importando mesmo com a
       * recusa desligada: sem ela, o score medido sairia de um frame borrado e o log
       * ficaria envenenado para a recalibração.
       *
       * Antes isto rodava solto, no
       * PRIMEIRO frame acumulado — o que sai poucos décimos depois de a câmera
       * abrir, antes de o autofoco fechar. E este modelo lê rosto DESFOCADO como
       * tela: medido fora do aparelho em 02/09/2026, sobre 60 rostos que ele
       * aceitava (score < 0,5), aplicando só um borrão gaussiano ao tile:
       *
       *   original            0,0% viram "ataque"
       *   desfoque r=2       88,3%
       *   desfoque r=4       98,3%
       *   escurecer x0,35    11,7%   <- exposição quase não pesa; foco é tudo
       *
       * Era isso que acusava de fraude quem estava com o rosto na frente da câmera.
       * O app JÁ sabia recusar foto borrada, com a mensagem certa ("Foto sem nitidez
       * — segure o aparelho firme"), só que essa porta rodava DEPOIS desta. Agora a
       * qualidade decide primeiro, e a prova de vida só opina sobre um rosto nítido
       * — que é o único caso em que o veredito dela significa alguma coisa.
       */
      const provaDeVida = async (
        candidatos: { uri: string; face: DetectedFace; nitidez: number }[],
      ): Promise<string | null> => {
        // Do mais nítido para o menos (`enrollFromUris` já ordenou).
        const alvos = candidatos.slice(0, FRAMES_PROVA_VIDA);
        if (alvos.length === 0) return null;

        // ⚠️ MEDE TODOS os alvos, sem atalho. O atalho antigo parava assim que a
        // MAIORIA estava decidida — o que servia para a regra de maioria e não serve
        // para esta: a recusa agora exige UNANIMIDADE, e unanimidade só se conhece
        // depois de medir todo mundo. Parar cedo também mutilaria a amostra do log,
        // que é o insumo da recalibração.
        const scores: number[] = [];

        for (const alvo of alvos) {
          try {
            // Reaproveita o rosto já detectado: em aparelho antigo, detectar de novo
            // custa mais que a própria inferência.
            const [spoofTile] = await TirvuFace.extractTiles(alvo.uri, alvo.face, [
              SPEC_ANTISPOOF,
            ]);
            const spoof = await FaceAntiSpoof.isRealFromTile(spoofTile);
            scores.push(spoof.score);
          } catch (err) {
            // Modelo ausente/quebrado NÃO pode impedir o cadastro — mas fica registrado.
            // Para com o que já mediu: as amostras que saíram ainda valem para o log.
            console.warn('[BIO] anti-spoof indisponível no cadastro:', err);
            break;
          }
        }

        // Nenhuma medição saiu: é o caso "modelo indisponível", que já falhava aberto.
        if (scores.length === 0) return null;

        const ordenados = [...scores].sort((a, b) => a - b);
        const meio = Math.floor(ordenados.length / 2);
        // Mediana e não média: com 2 amostras dá no mesmo, com 3+ resiste melhor a um
        // único frame delirante, que é exatamente o modo de falha medido.
        spoofScore =
          ordenados.length % 2 === 1
            ? ordenados[meio]
            : (ordenados[meio - 1] + ordenados[meio]) / 2;

        /* Recusa por UNANIMIDADE acima do limiar ALTO — e hoje nem isso, porque a
         * prova de vida está DESLIGADA (ver `PROVA_DE_VIDA_ATIVA`).
         *
         * A regra anterior aqui era a MAIORIA passar de 0,90, e ela não resolvia o
         * caso que gerou os relatos: os 3 frames do cadastro saem a 220 ms um do
         * outro, com a pessoa parada — porque é isso que a tela manda fazer. Três
         * imagens quase idênticas dão três scores quase idênticos, então a "maioria"
         * é sempre unânime e decidir por 3 vale o mesmo que decidir por 1.
         *
         * É a CONSISTÊNCIA, e não o pico, que separaria vivo de ataque: no conjunto
         * medido o mesmo rosto vivo variava de −0,006 a 0,323, enquanto o ataque era
         * alto de forma consistente. Um único frame abaixo do limiar já é evidência
         * de rosto vivo.
         */
        const todasAcima = scores.every(
          (s) => s > FaceAntiSpoof.CONFIG.REFUSE_THRESHOLD,
        );
        const recusar = FaceAntiSpoof.CONFIG.PROVA_DE_VIDA_ATIVA && todasAcima;

        // `warn` e não `log`: o babel remove log/info/debug do build de produção
        // (ver babel.config.js), e este é justamente o número que alimenta a
        // recalibração — sem ele o suporte fica no palpite.
        console.warn(
          `[BIO] prova de vida ${
            FaceAntiSpoof.CONFIG.PROVA_DE_VIDA_ATIVA ? 'ATIVA' : 'desligada'
          }: mediana ${spoofScore.toFixed(4)} ` +
            `(medição ${FaceAntiSpoof.CONFIG.ATTACK_THRESHOLD}, recusa ${FaceAntiSpoof.CONFIG.REFUSE_THRESHOLD}) · ` +
            `scores [${scores.map((s) => s.toFixed(3)).join(', ')}] · ` +
            `nitidez ${alvos.map((a) => a.nitidez.toFixed(0)).join('/')} · ` +
            `${candidatos.length} de ${imageUris.length} foto(s) aprovadas · ` +
            `${recusar ? 'RECUSADO' : 'aprovado'}`,
        );

        return recusar
          ? 'Use seu rosto real para cadastrar a biometria — não pode ser foto ou tela.'
          : null;
      };

      const res = await FaceRecognition.enrollFromUris(imageUris, {
        antesDeGravar: opts?.antiSpoof === false ? undefined : provaDeVida,
        // O rosto que a tela já detectou vale para a PRIMEIRA foto (é dela que ele
        // saiu). As telas de ponto e de diária sempre passaram `face` aqui, com um
        // comentário dizendo que isso evitava uma segunda detecção — mas até então
        // `cadastrar` só repassava esse campo em `verificar`, e no cadastro a foto era
        // detectada de novo. Agora a promessa do comentário é verdade.
        face: opts?.face,
      });

      // ── Sobe para o banco ──────────────────────────────────────────────
      //
      // É o que faz a biometria valer FORA deste aparelho: gravada no banco, ela
      // entra na galeria do coletor TirvuTab do posto, e o colaborador que
      // cadastrou no celular bate ponto no tablet sem cadastrar de novo.
      //
      // `modo_cadastro: 'C'` = cadastrado no app do colaborador.
      //
      // ⚠️ Hoje este campo é DECORATIVO: o `T-Consumer.CadastrarAsync` grava
      // `modo_cadastro = 'C'` fixo, ignorando o que vem na mensagem. Mandamos 'C'
      // mesmo assim porque é a verdade — se um dia o consumidor passar a honrar o
      // campo, o valor já está certo, e enquanto isso o payload não mente.
      //
      // Quem faz a biometria aparecer no coletor é a leitura, não a escrita: a
      // `ListaBiometrias` do T-ARC aceita 'T' e 'C' (era só 'T', e por isso a
      // biometria cadastrada no celular ficava invisível no posto).
      //
      // ⚠️ NÃO derruba o cadastro se a subida falhar. O cadastro LOCAL já está
      // gravado e o colaborador já consegue bater ponto neste aparelho — perder
      // isso por uma falha de rede seria trocar um problema pequeno (a biometria
      // demora a chegar no coletor) por um grande (não cadastrou nada).
      if (res.enrolled && opts?.identidade) {
        try {
          const exportado = await FaceRecognition.exportarParaBanco();
          if (exportado) {
            const resp = await Api.facial.cadastraFace({
              id_adm_colaborador: opts.identidade.id_adm_colaborador,
              // ── Posto e dispositivo ZERADOS, de propósito ──────────────────
              //
              // A foto tirada no app não pertence a um posto nem a um aparelho: ela é
              // da PESSOA, e vale em qualquer coletor onde ela possa bater ponto. É
              // esse o modo de armazenamento do TirvuTab — a galeria do coletor é
              // derivada da LOTAÇÃO do colaborador (`ListaBiometrias` no T-ARC filtra
              // por `tab_adm_colaboradores.id_adm_cliente`, com rodízio e
              // transferência agendada), e não por onde a foto foi tirada.
              //
              // Gravar o posto aqui é o que fazia a biometria não acompanhar quem é
              // transferido: nada na transferência toca `tab_adm_colaboradores_facial`.
              //
              // ⚠️ Quem GARANTE os dois zeros é o `INSERT` do T-Consumer
              // (`FacialRepository.CadastrarAsync`), que fixa `id_adm_cliente = 0` e
              // `id_dispositivo = 0` no próprio SQL — e é lá que tem de continuar,
              // porque mensagem antiga parada na fila carrega o posto real: um
              // consumidor que honrasse o payload gravaria posto errado durante todo o
              // rollout de uma versão do app.
              //
              // Aqui o 0 existe só para o payload parar de afirmar o contrário do que é
              // gravado. E `id_dispositivo` NÃO é enviado de propósito: o campo não
              // existe no `ColaboradorFacial` do T-ARC, seria descartado no primeiro
              // hop, e um campo que o remetente manda e ninguém recebe é exatamente o
              // que já aconteceu com o `descritor` — mandaram, o log dizia "pronta para
              // o coletor", e o valor morria antes da fila.
              id_adm_cliente: 0,
              cpf: opts.identidade.cpf,
              foto: exportado.foto,
              descritor: exportado.descritor,
              modo_cadastro: 'C',
            });
            if (resp?.success) {
              console.log(
                '[BIO] Biometria enviada ao banco' +
                  (exportado.descritor
                    ? ' com descritor ArcFace (pronta para o coletor).'
                    : ' SÓ COM A FOTO — o coletor vai reprocessar para gerar o descritor.'),
              );
            } else {
              console.warn('[BIO] Biometria não subiu para o banco:', resp?.erros);
            }
          } else {
            console.warn('[BIO] Nada a exportar (sem cadastro local legível).');
          }
        } catch (err) {
          console.warn('[BIO] Falha ao subir a biometria:', err);
        }
      }

      return {
        ok: res.enrolled,
        motivo: res.reason,
        spoofScore,
        qualidade: null,
      };
    } catch (err: any) {
      return {
        ok: false,
        motivo: err?.message ?? 'Falha ao cadastrar a biometria',
        spoofScore: null,
        qualidade: null,
      };
    }
  },

  /**
   * Verifica a identidade e, de quebra, classifica a expressão — numa passada só.
   *
   * `antiSpoof` vem DESLIGADO por padrão: no fluxo atual do ponto ele roda apenas no
   * cadastro (o bloco da verificação está comentado em `screens/Camera.tsx` desde
   * antes desta mudança). Ligar aqui muda a política de quem consegue bater ponto, e
   * isso é decisão de produto — não efeito colateral de refatoração. Se ligar,
   * recalibre o ATTACK_THRESHOLD antes (ver `services/FaceAntiSpoof.ts`).
   */
  verificar: async (
    imageUri: string,
    opts?: Opcoes,
  ): Promise<ResultadoVerificacao> => {
    const vazio: ResultadoVerificacao = {
      ok: false,
      similarity: 0,
      duvidoso: false,
      temReferencia: true,
      spoofScore: null,
      expressao: null,
      qualidade: null,
    };

    try {
      const face = opts?.face ?? (await detectarMaior(imageUri));

      const querSpoof = opts?.antiSpoof === true;
      const querExpressao = opts?.expressao !== false;
      const specs: TileSpec[] = [SPEC_RECONHECIMENTO];
      if (querSpoof) specs.push(SPEC_ANTISPOOF);
      if (querExpressao) specs.push(SPEC_EXPRESSAO);

      // UMA chamada nativa para todos os tiles deste rosto.
      const tiles: Tile[] = await TirvuFace.extractTiles(imageUri, face, specs);
      let i = 0;
      const tileRec = tiles[i++];
      const tileSpoof = querSpoof ? tiles[i++] : null;
      const tileExpr = querExpressao ? tiles[i++] : null;

      let spoofScore: number | null = null;
      if (tileSpoof) {
        try {
          const spoof = await FaceAntiSpoof.isRealFromTile(tileSpoof);
          spoofScore = spoof.score;
          if (!spoof.real) {
            return {
              ...vazio,
              motivo: `Score de ataque ${spoof.score.toFixed(2)} (limite ${FaceAntiSpoof.CONFIG.ATTACK_THRESHOLD}). Use seu rosto, não uma foto ou tela.`,
              spoofScore,
            };
          }
        } catch (err) {
          console.warn('[BIO] anti-spoof indisponível na verificação:', err);
        }
      }

      const referencias = await FaceRecognition.loadReference();
      if (!referencias) {
        return { ...vazio, temReferencia: false, motivo: 'Sem biometria cadastrada', spoofScore };
      }

      const embedding = await FaceRecognition.embedFromTile(tileRec);
      let similarity = -1;
      for (const ref of referencias) {
        const s = FaceRecognition.compare(ref, embedding);
        if (s > similarity) similarity = s;
      }

      const expressao = tileExpr ? await FaceExpression.classifyFromTile(tileExpr) : null;
      const matched = FaceRecognition.isMatch(similarity);
      const duvidoso = !matched && similarity >= FaceRecognition.CONFIG.GRAY_ZONE;

      // Fica no console em produção de propósito (o babel só remove log/info/debug):
      // é este número que se usa para calibrar o threshold em campo.
      console.warn(
        `[BIO] similaridade=${similarity.toFixed(3)} limite=${FaceRecognition.CONFIG.THRESHOLD} match=${matched}`,
      );

      return {
        ok: matched,
        motivo: matched
          ? undefined
          : duvidoso
            ? 'Não deu para confirmar seu rosto. Tente de novo em um local mais iluminado.'
            : `O rosto não corresponde ao cadastrado na biometria (similaridade ${(similarity * 100).toFixed(0)}%). Só o próprio colaborador pode bater o ponto.`,
        similarity,
        duvidoso,
        temReferencia: true,
        spoofScore,
        expressao,
        qualidade: null,
      };
    } catch (err: any) {
      return { ...vazio, motivo: err?.message ?? 'Falha ao processar o reconhecimento facial' };
    }
  },
};
